import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ChevronDown } from 'lucide-react';
import { Button, Dialog, DialogTrigger } from 'react-aria-components';
import { Popover } from '../tailgrids/core/popover.js';
import { cn } from '../../utils/cn.js';

/**
 * A toolbar setting that opens its picker (TradingView's Strategy Tester toolbar: "📅 Oct 12 — Oct 19 ▾", "$ 100K ▾"; NextAdmin
 * `ui-elements/dropdowns` trigger, `ui-elements/popovers` panel): the trigger shows the icon, the current value and a
 * chevron; the panel holds presets or a small form, which closes it through `close()` once applied. Keyboard
 * reachable (Tab, Enter/Space), closes on Escape or an outside click. A read-only setting renders the value alone.
 */
export function ToolbarPopover({
  icon: Icon,
  label,
  value,
  children,
  readOnly = false,
  className,
}: {
  icon: LucideIcon;
  /** Names the setting for screen readers ("Backtest date range"). */
  label: string;
  /** The current value, shown on the trigger. */
  value: ReactNode;
  /** The picker; `close` dismisses the panel. */
  children: (close: () => void) => ReactNode;
  /** Shows the value without a picker (a viewer without the right to change it). */
  readOnly?: boolean;
  className?: string;
}) {
  const content = (
    <>
      <Icon className="size-4 shrink-0 text-text-secondary" aria-hidden />
      <span className="truncate">{value}</span>
    </>
  );
  if (readOnly) {
    return (
      <span
        aria-label={label}
        className={cn('inline-flex min-w-0 items-center gap-1.5 px-2 py-1 text-sm text-text-primary', className)}
      >
        {content}
      </span>
    );
  }
  return (
    <DialogTrigger>
      <Button
        aria-label={label}
        className={cn(
          'inline-flex min-w-0 items-center gap-1.5 rounded-md px-2 py-1 text-sm text-text-primary outline-none transition-colors hover:bg-tab-secondary-active-background focus-visible:ring-2 focus-visible:ring-accent/40 data-[pressed]:bg-tab-secondary-active-background',
          className,
        )}
      >
        {content}
        <ChevronDown className="size-3.5 shrink-0 text-text-secondary" aria-hidden />
      </Button>
      {/* `bg-card-background`: the vendored popover paints `background-100` (#111827 in dark), not a NextAdmin surface. */}
      <Popover placement="bottom start" className="max-h-[70vh] w-72 overflow-auto bg-card-background p-3 shadow-lg">
        <Dialog aria-label={label} className="text-sm text-text-primary outline-none">
          {({ close }) => children(close)}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
